import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { lookupSkuByBarcode, Sku } from '../../../services/skusApi';
import {
  createPosSale, quotePosSale, listPosSales, voidPosSale, openPosShift, closePosShift,
  PosSaleResult, PosPaymentMethod, PosQuote, PosSaleListRow, PosShift,
} from '../../../services/posApi';
import { useAuth } from '../../../contexts/AuthContext';
import { Modal } from '../../../design-system';
import styles from './CounterSalesPOS.module.css';

/** One line in the running counter-sale cart. */
interface CartLine {
  sku_id: number;
  sku_code: string;
  name: string;
  list_price: number;
  unit_price: number;
  quantity: number;
  total_stock: number;
}

function money(n: number): string {
  return `$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
}

// The open drawer shift is tracked client-side (the backend has no "get my
// open shift" lookup endpoint — only open/close), persisted per-user so a
// page refresh mid-shift doesn't lose track of it.
function shiftStorageKey(userId?: string): string {
  return `pos_open_shift_${userId ?? 'anon'}`;
}

const CounterSalesPOS: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';

  const [cart, setCart] = useState<CartLine[]>([]);
  const [barcode, setBarcode] = useState('');
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [looking, setLooking] = useState(false);

  const [buyerName, setBuyerName] = useState('');
  const [buyerMobile, setBuyerMobile] = useState('');
  const [method, setMethod] = useState<PosPaymentMethod>('cash');
  const [tendered, setTendered] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [saleError, setSaleError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<PosSaleResult | null>(null);

  // Manager-override audit trail (VP-POS-001) — required by the backend
  // whenever a line's price is dropped below the SKU's list price.
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');

  // Cash-drawer shift (VP-POS-001).
  const [shift, setShift] = useState<PosShift | null>(null);
  const [shiftModal, setShiftModal] = useState<'open' | 'close' | null>(null);
  const [openingFloat, setOpeningFloat] = useState('');
  const [countedCash, setCountedCash] = useState('');
  const [shiftBusy, setShiftBusy] = useState(false);
  const [shiftError, setShiftError] = useState<string | null>(null);
  const [closedSummary, setClosedSummary] = useState<PosShift | null>(null);

  // Recent sales / void (VP-POS-001, Admin-only).
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<PosSaleListRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [voidTarget, setVoidTarget] = useState<PosSaleListRow | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidBusy, setVoidBusy] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);

  // Server-priced quote (subtotal + warehouse-jurisdiction tax + total), refreshed as the cart changes.
  const [quote, setQuote] = useState<PosQuote | null>(null);
  const [quoting, setQuoting] = useState(false);
  // sku_id → product name, captured at sale time so the receipt shows names (the
  // sale result only carries sku_id).
  const [soldNames, setSoldNames] = useState<Record<number, string>>({});

  const scanRef = useRef<HTMLInputElement>(null);

  const focusScan = useCallback(() => {
    // Defer so it runs after the current render/clear.
    setTimeout(() => scanRef.current?.focus(), 0);
  }, []);

  // Restore an in-progress drawer shift on mount / when the user changes.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(shiftStorageKey(user?.id));
      if (raw) setShift(JSON.parse(raw));
    } catch {
      // ignore — no persisted shift, or storage unavailable
    }
  }, [user?.id]);

  const persistShift = (s: PosShift | null) => {
    setShift(s);
    try {
      if (s) localStorage.setItem(shiftStorageKey(user?.id), JSON.stringify(s));
      else localStorage.removeItem(shiftStorageKey(user?.id));
    } catch {
      // ignore
    }
  };

  const addSkuToCart = useCallback((sku: Sku) => {
    if (sku.list_price == null || sku.list_price <= 0) {
      setLookupError(`"${sku.product_name}" has no sellable price set.`);
      return;
    }
    setCart((prev) => {
      const existing = prev.find((l) => l.sku_id === sku.id);
      if (existing) {
        return prev.map((l) =>
          l.sku_id === sku.id ? { ...l, quantity: l.quantity + 1 } : l
        );
      }
      return [
        ...prev,
        {
          sku_id: sku.id,
          sku_code: sku.sku_code,
          name: sku.product_name,
          list_price: sku.list_price as number,
          unit_price: sku.list_price as number,
          quantity: 1,
          total_stock: sku.total_stock,
        },
      ];
    });
  }, []);

  const handleScan = useCallback(
    async (code: string) => {
      const trimmed = code.trim();
      if (!trimmed) return;
      setLookupError(null);
      setLooking(true);
      try {
        const sku = await lookupSkuByBarcode(trimmed);
        addSkuToCart(sku);
        setBarcode('');
      } catch (err: any) {
        const msg =
          err?.response?.data?.error?.message ||
          err?.message ||
          `No product found for "${trimmed}"`;
        setLookupError(msg);
      } finally {
        setLooking(false);
        focusScan();
      }
    },
    [addSkuToCart, focusScan]
  );

  const onScanKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleScan(barcode);
    }
  };

  const setQty = (sku_id: number, qty: number) => {
    setCart((prev) =>
      prev
        .map((l) => (l.sku_id === sku_id ? { ...l, quantity: Math.max(0, qty) } : l))
        .filter((l) => l.quantity > 0)
    );
  };

  const setPrice = (sku_id: number, price: number) => {
    setCart((prev) =>
      prev.map((l) => (l.sku_id === sku_id ? { ...l, unit_price: Math.max(0, price) } : l))
    );
  };

  const removeLine = (sku_id: number) => {
    setCart((prev) => prev.filter((l) => l.sku_id !== sku_id));
  };

  const subtotal = useMemo(
    () => cart.reduce((sum, l) => sum + l.unit_price * l.quantity, 0),
    [cart]
  );

  // Any line priced below the SKU's list price requires a manager-override
  // reason before the sale can complete (VP-POS-001 — enforced server-side too).
  const hasDiscount = useMemo(() => cart.some((l) => l.unit_price < l.list_price), [cart]);

  // A stable key of the cart contents so the quote effect only re-runs on real changes.
  const cartKey = useMemo(
    () => cart.map((l) => `${l.sku_id}:${l.quantity}:${l.unit_price}`).join('|'),
    [cart]
  );

  // Fetch an authoritative quote (incl. tax) whenever the cart changes (debounced).
  useEffect(() => {
    if (cart.length === 0) { setQuote(null); setQuoting(false); return; }
    let cancelled = false;
    setQuoting(true);
    const t = setTimeout(() => {
      quotePosSale(cart.map((l) => ({ sku_id: l.sku_id, quantity: l.quantity, unit_price: l.unit_price })))
        .then((q) => { if (!cancelled) setQuote(q); })
        .catch(() => { if (!cancelled) setQuote(null); })
        .finally(() => { if (!cancelled) setQuoting(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartKey]);

  const tenderedNum = parseFloat(tendered);
  // The amount actually due (tax included) once the quote lands; subtotal until then.
  const totalDue = quote ? quote.total_amount : subtotal;
  const change =
    method === 'cash' && !isNaN(tenderedNum) ? tenderedNum - totalDue : 0;

  const overStockLine = cart.find((l) => l.quantity > l.total_stock);

  const canComplete =
    cart.length > 0 &&
    !submitting &&
    !quoting &&
    !!quote &&
    (method !== 'cash' || (!isNaN(tenderedNum) && tenderedNum >= totalDue));

  const runSale = async (overrideInput?: { reason: string }) => {
    setSubmitting(true);
    setSaleError(null);
    try {
      const result = await createPosSale({
        items: cart.map((l) => ({
          sku_id: l.sku_id,
          quantity: l.quantity,
          unit_price: l.unit_price,
        })),
        payment_method: method,
        buyer_name: buyerName.trim() || undefined,
        buyer_mobile: buyerMobile.trim() || undefined,
        amount_tendered: method === 'cash' ? tenderedNum : undefined,
        price_override_reason: overrideInput?.reason,
        price_override_by_user_id: overrideInput ? (user ? Number(user.id) : undefined) : undefined,
      });
      setSoldNames(Object.fromEntries(cart.map((l) => [l.sku_id, l.name])));
      setReceipt(result);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.message ||
        'Failed to complete the sale.';
      setSaleError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const completeSale = async () => {
    if (cart.length === 0) return;
    // A below-list-price line needs a recorded reason before we submit —
    // matches the backend's OVERRIDE_REQUIRED gate (createPosSale).
    if (hasDiscount) {
      setOverrideReason('');
      setShowOverrideModal(true);
      return;
    }
    await runSale();
  };

  const confirmOverride = async () => {
    if (!overrideReason.trim()) return;
    setShowOverrideModal(false);
    await runSale({ reason: overrideReason.trim() });
  };

  const newSale = () => {
    setReceipt(null);
    setCart([]);
    setBarcode('');
    setBuyerName('');
    setBuyerMobile('');
    setMethod('cash');
    setTendered('');
    setSaleError(null);
    setLookupError(null);
    focusScan();
  };

  // ---- Drawer shift handlers --------------------------------------------
  const openShift = async () => {
    const val = parseFloat(openingFloat);
    if (isNaN(val) || val < 0) { setShiftError('Enter a valid starting float.'); return; }
    setShiftBusy(true);
    setShiftError(null);
    try {
      const s = await openPosShift(val);
      persistShift(s);
      setShiftModal(null);
      setOpeningFloat('');
    } catch (err: any) {
      setShiftError(err?.response?.data?.error?.message || err?.message || 'Failed to open drawer shift.');
    } finally {
      setShiftBusy(false);
    }
  };

  const closeShift = async () => {
    if (!shift) return;
    const val = parseFloat(countedCash);
    if (isNaN(val) || val < 0) { setShiftError('Enter the counted cash amount.'); return; }
    setShiftBusy(true);
    setShiftError(null);
    try {
      const closed = await closePosShift(shift.id, val);
      persistShift(null);
      setClosedSummary(closed);
      setShiftModal(null);
      setCountedCash('');
    } catch (err: any) {
      setShiftError(err?.response?.data?.error?.message || err?.message || 'Failed to close drawer shift.');
    } finally {
      setShiftBusy(false);
    }
  };

  // ---- Recent sales / void (Admin-only) ----------------------------------
  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await listPosSales({ limit: 10 });
      setHistory(res.data);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next) loadHistory();
  };

  const confirmVoid = async () => {
    if (!voidTarget || !voidReason.trim()) return;
    setVoidBusy(true);
    setVoidError(null);
    try {
      await voidPosSale(voidTarget.id, voidReason.trim());
      setVoidTarget(null);
      setVoidReason('');
      await loadHistory();
    } catch (err: any) {
      setVoidError(err?.response?.data?.error?.message || err?.message || 'Failed to void sale.');
    } finally {
      setVoidBusy(false);
    }
  };

  // ---- Receipt view ----------------------------------------------------------
  if (receipt) {
    const itemName = (skuId: number) => soldNames[skuId] || `Item #${skuId}`;
    const methodLabel = { cash: 'Cash', card: 'Card', ach: 'ACH', check: 'Check' }[receipt.payment_method] || receipt.payment_method;
    return (
      <div className={styles.receiptScreen}>
        <div className={styles.invoice} id="pos-receipt">
          <div className={styles.invoiceTop}>
            <div className={styles.successBadge} aria-hidden>
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <h2 className={styles.invoiceHeading}>Payment Successful</h2>
            <div className={styles.invoiceBigTotal}>{money(receipt.total_amount)}</div>
            <div className={styles.invoicePaidVia}>Paid via {methodLabel}</div>
          </div>

          <div className={styles.invoiceMeta}>
            <div><span>Invoice</span><strong>{receipt.invoice_number}</strong></div>
            <div><span>Order</span><strong>#{receipt.order_id}</strong></div>
            <div><span>Date</span><strong>{new Date(receipt.sold_at).toLocaleString()}</strong></div>
            {receipt.buyer_name && <div><span>Customer</span><strong>{receipt.buyer_name}</strong></div>}
            {receipt.buyer_mobile && <div><span>Mobile</span><strong>{receipt.buyer_mobile}</strong></div>}
          </div>

          <div className={styles.invoiceItems}>
            {receipt.items.map((it) => (
              <div key={it.sku_id} className={styles.invoiceItem}>
                <div className={styles.invoiceItemMain}>
                  <span className={styles.invoiceItemName}>{itemName(it.sku_id)}</span>
                  <span className={styles.invoiceItemSub}>{it.quantity} × {money(it.unit_price)}</span>
                </div>
                <span className={styles.invoiceItemTotal}>{money(it.line_total)}</span>
              </div>
            ))}
          </div>

          <div className={styles.invoiceTotals}>
            <div><span>Subtotal</span><span>{money(receipt.subtotal)}</span></div>
            <div><span>Tax</span><span>{money(receipt.tax_total)}</span></div>
            <div className={styles.invoiceGrand}><span>Total</span><span>{money(receipt.total_amount)}</span></div>
            {receipt.payment_method === 'cash' && (
              <>
                <div className={styles.invoiceMutedRow}><span>Cash tendered</span><span>{money(receipt.amount_tendered ?? 0)}</span></div>
                <div className={styles.invoiceChange}><span>Change due</span><span>{money(receipt.change_due)}</span></div>
              </>
            )}
          </div>

          <p className={styles.invoiceThanks}>Thank you for your purchase!</p>
        </div>

        <div className={styles.receiptActions}>
          <button className={styles.secondaryBtn} onClick={() => window.print()}>Print</button>
          <button className={styles.primaryBtn} onClick={newSale}>New Sale</button>
        </div>
      </div>
    );
  }

  // ---- Register view ---------------------------------------------------------
  return (
    <div className={styles.container}>
      <div className={styles.drawerBar}>
        <div className={styles.drawerStatus}>
          {shift ? (
            <span>Drawer open — float {money(shift.opening_float)} (since {new Date(shift.opened_at).toLocaleTimeString()})</span>
          ) : (
            <span className={styles.muted}>Drawer closed</span>
          )}
        </div>
        <div className={styles.drawerActions}>
          {!shift ? (
            <button className={styles.secondaryBtn} onClick={() => { setShiftError(null); setShiftModal('open'); }}>
              Open Drawer
            </button>
          ) : (
            <button className={styles.secondaryBtn} onClick={() => { setShiftError(null); setShiftModal('close'); }}>
              Close Drawer
            </button>
          )}
          <button className={styles.secondaryBtn} onClick={toggleHistory}>
            {showHistory ? 'Hide Recent Sales' : 'Recent Sales'}
          </button>
        </div>
      </div>

      {showHistory && (
        <div className={styles.historyPanel}>
          <h4>Recent Counter Sales</h4>
          {historyLoading ? (
            <p className={styles.muted}>Loading…</p>
          ) : history.length === 0 ? (
            <p className={styles.muted}>No counter sales yet.</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Buyer</th>
                  <th>Method</th>
                  <th className={styles.num}>Total</th>
                  <th>Status</th>
                  {isAdmin && <th />}
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id}>
                    <td>#{h.id} {h.invoice_number ? `(${h.invoice_number})` : ''}</td>
                    <td>{h.buyer_name || '—'}</td>
                    <td>{h.payment_method || '—'}</td>
                    <td className={styles.num}>{money(h.total_amount)}</td>
                    <td>{h.status}</td>
                    {isAdmin && (
                      <td className={styles.num}>
                        {h.status !== 'cancelled' && (
                          <button
                            className={styles.removeBtn}
                            onClick={() => { setVoidTarget(h); setVoidReason(''); setVoidError(null); }}
                          >
                            Void
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className={styles.registerGrid}>
        {/* Left: scan + cart */}
        <div className={styles.cartPanel}>
          <div className={styles.scanRow}>
            <input
              ref={scanRef}
              autoFocus
              className={styles.scanInput}
              placeholder="Scan or type barcode / SKU, then press Enter"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              onKeyDown={onScanKeyDown}
            />
            <button
              className={styles.secondaryBtn}
              disabled={looking || !barcode.trim()}
              onClick={() => handleScan(barcode)}
            >
              {looking ? 'Looking…' : 'Add'}
            </button>
          </div>
          {lookupError && <div className={styles.errorBanner}>{lookupError}</div>}

          {cart.length === 0 ? (
            <div className={styles.empty}>Cart is empty. Scan a product to begin.</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Product</th>
                  <th className={styles.num}>Unit</th>
                  <th className={styles.num}>Qty</th>
                  <th className={styles.num}>Line</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {cart.map((l) => (
                  <tr key={l.sku_id}>
                    <td>
                      <div className={styles.prodName}>{l.name}</div>
                      <div className={styles.prodSku}>{l.sku_code}</div>
                      {l.quantity > l.total_stock && (
                        <div className={styles.warn}>Only {l.total_stock} in stock</div>
                      )}
                    </td>
                    <td className={styles.num}>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        className={styles.priceInput}
                        value={l.unit_price}
                        onChange={(e) => setPrice(l.sku_id, parseFloat(e.target.value) || 0)}
                        aria-label={`Unit price for ${l.name}`}
                      />
                      {l.unit_price < l.list_price && (
                        <div className={styles.warn}>below list ({money(l.list_price)})</div>
                      )}
                    </td>
                    <td className={styles.num}>
                      <div className={styles.qtyStepper}>
                        <button
                          onClick={() => setQty(l.sku_id, l.quantity - 1)}
                          aria-label={`Decrease quantity for ${l.name}`}
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min={1}
                          value={l.quantity}
                          onChange={(e) => setQty(l.sku_id, parseInt(e.target.value, 10) || 0)}
                          aria-label={`Quantity for ${l.name}`}
                        />
                        <button
                          onClick={() => setQty(l.sku_id, l.quantity + 1)}
                          aria-label={`Increase quantity for ${l.name}`}
                        >
                          +
                        </button>
                      </div>
                    </td>
                    <td className={styles.num}>{money(l.unit_price * l.quantity)}</td>
                    <td className={styles.num}>
                      <button
                        className={styles.removeBtn}
                        onClick={() => removeLine(l.sku_id)}
                        aria-label={`Remove ${l.name} from cart`}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Right: checkout */}
        <div className={styles.checkoutPanel}>
          <h3>Checkout</h3>
          <label className={styles.field}>
            <span>Buyer name</span>
            <input value={buyerName} onChange={(e) => setBuyerName(e.target.value)} placeholder="Optional" />
          </label>
          <label className={styles.field}>
            <span>Mobile number</span>
            <input value={buyerMobile} onChange={(e) => setBuyerMobile(e.target.value)} placeholder="Optional" />
          </label>

          <div className={styles.field}>
            <span>Payment method</span>
            <div className={styles.methodToggle}>
              <button
                className={method === 'cash' ? styles.methodActive : ''}
                onClick={() => setMethod('cash')}
              >
                Cash
              </button>
              <button
                className={method === 'card' ? styles.methodActive : ''}
                onClick={() => setMethod('card')}
              >
                Card
              </button>
              <button
                className={method === 'ach' ? styles.methodActive : ''}
                onClick={() => setMethod('ach')}
              >
                ACH
              </button>
              <button
                className={method === 'check' ? styles.methodActive : ''}
                onClick={() => setMethod('check')}
              >
                Check
              </button>
            </div>
          </div>

          {method === 'cash' && (
            <label className={styles.field}>
              <span>Amount tendered</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={tendered}
                onChange={(e) => setTendered(e.target.value)}
                placeholder="0.00"
              />
            </label>
          )}

          <div className={styles.summary}>
            <div><span>Subtotal</span><span>{money(quote ? quote.subtotal : subtotal)}</span></div>
            <div className={quote ? undefined : styles.muted}>
              <span>Tax</span>
              <span>{quoting ? 'calculating…' : quote ? money(quote.tax_total) : '—'}</span>
            </div>
            <div className={styles.grand}>
              <span>Total</span>
              <span>{quoting ? 'calculating…' : money(totalDue)}</span>
            </div>
            {method === 'cash' && !isNaN(tenderedNum) && (
              <div className={styles.change}>
                <span>Change</span>
                <span>{money(change)}</span>
              </div>
            )}
          </div>

          {hasDiscount && (
            <div className={styles.warn} style={{ marginBottom: 8 }}>
              One or more items are priced below list — a manager-override reason will be required.
            </div>
          )}
          {overStockLine && (
            <div className={styles.errorBanner}>
              {overStockLine.name}: requested qty exceeds stock ({overStockLine.total_stock}).
            </div>
          )}
          {saleError && <div className={styles.errorBanner}>{saleError}</div>}

          <button className={styles.primaryBtn} disabled={!canComplete} onClick={completeSale}>
            {submitting ? 'Processing…' : 'Complete Sale'}
          </button>
        </div>
      </div>

      {/* Manager-override reason modal (VP-POS-001) */}
      <Modal
        isOpen={showOverrideModal}
        onClose={() => setShowOverrideModal(false)}
        title="Manager Override Required"
        size="sm"
        footer={
          <>
            <button className={styles.secondaryBtn} onClick={() => setShowOverrideModal(false)}>Cancel</button>
            <button className={styles.primaryBtn} disabled={!overrideReason.trim()} onClick={confirmOverride} style={{ marginTop: 0, width: 'auto' }}>
              Authorize &amp; Complete Sale
            </button>
          </>
        }
      >
        <p>One or more items are priced below list. Enter a reason to authorize this sale — it will be recorded on the order for audit.</p>
        <label className={styles.field}>
          <span>Override reason</span>
          <textarea
            rows={3}
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            placeholder="e.g. price-match, damaged packaging, loyalty discount…"
            style={{ padding: 10, borderRadius: 8, border: '1px solid #CBD5E1', font: 'inherit' }}
          />
        </label>
      </Modal>

      {/* Drawer open/close modal (VP-POS-001) */}
      <Modal
        isOpen={shiftModal !== null}
        onClose={() => setShiftModal(null)}
        title={shiftModal === 'open' ? 'Open Cash Drawer' : 'Close Cash Drawer'}
        size="sm"
        footer={
          <>
            <button className={styles.secondaryBtn} onClick={() => setShiftModal(null)}>Cancel</button>
            {shiftModal === 'open' ? (
              <button className={styles.primaryBtn} disabled={shiftBusy} onClick={openShift} style={{ marginTop: 0, width: 'auto' }}>
                {shiftBusy ? 'Opening…' : 'Open Drawer'}
              </button>
            ) : (
              <button className={styles.primaryBtn} disabled={shiftBusy} onClick={closeShift} style={{ marginTop: 0, width: 'auto' }}>
                {shiftBusy ? 'Closing…' : 'Close Drawer'}
              </button>
            )}
          </>
        }
      >
        {shiftModal === 'open' ? (
          <label className={styles.field}>
            <span>Starting float</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={openingFloat}
              onChange={(e) => setOpeningFloat(e.target.value)}
              placeholder="0.00"
            />
          </label>
        ) : (
          <label className={styles.field}>
            <span>Counted cash in drawer</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={countedCash}
              onChange={(e) => setCountedCash(e.target.value)}
              placeholder="0.00"
            />
          </label>
        )}
        {shiftError && <div className={styles.errorBanner}>{shiftError}</div>}
      </Modal>

      {/* Post-close settlement summary */}
      <Modal
        isOpen={!!closedSummary}
        onClose={() => setClosedSummary(null)}
        title="Drawer Closed"
        size="sm"
        footer={<button className={styles.primaryBtn} onClick={() => setClosedSummary(null)} style={{ marginTop: 0, width: 'auto' }}>Done</button>}
      >
        {closedSummary && (
          <div className={styles.summary}>
            <div><span>Expected cash</span><span>{money(closedSummary.expected_cash ?? 0)}</span></div>
            <div><span>Counted cash</span><span>{money(closedSummary.counted_cash ?? 0)}</span></div>
            <div className={styles.grand}>
              <span>Variance</span>
              <span style={{ color: (closedSummary.variance ?? 0) === 0 ? '#16A34A' : '#DC2626' }}>
                {money(closedSummary.variance ?? 0)}
              </span>
            </div>
          </div>
        )}
      </Modal>

      {/* Void reason modal (Admin-only) */}
      <Modal
        isOpen={!!voidTarget}
        onClose={() => setVoidTarget(null)}
        title={voidTarget ? `Void Sale #${voidTarget.id}` : 'Void Sale'}
        size="sm"
        footer={
          <>
            <button className={styles.secondaryBtn} onClick={() => setVoidTarget(null)}>Cancel</button>
            <button className={styles.primaryBtn} disabled={!voidReason.trim() || voidBusy} onClick={confirmVoid} style={{ marginTop: 0, width: 'auto' }}>
              {voidBusy ? 'Voiding…' : 'Confirm Void'}
            </button>
          </>
        }
      >
        <p>Voiding refunds the recorded payment and cancels the order. This requires Admin authorization and is recorded for audit.</p>
        <label className={styles.field}>
          <span>Void reason</span>
          <textarea
            rows={3}
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            placeholder="Reason for voiding this sale…"
            style={{ padding: 10, borderRadius: 8, border: '1px solid #CBD5E1', font: 'inherit' }}
          />
        </label>
        {voidError && <div className={styles.errorBanner}>{voidError}</div>}
      </Modal>
    </div>
  );
};

export const CounterSalesPOSPage: React.FC = () => <CounterSalesPOS />;
export default CounterSalesPOS;
